import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch, Controller } from 'react-hook-form';
import {
  CreateConsultFirstStepFormSchema,
  CreateConsultFirstStepMutation,
} from './CreateConsultFirstStepForm.types';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  INIT_CONSULTATION_QUERY_KEY,
  queryInitConsultation,
  queryUpdateConsultation,
} from './CreateConsultFirstStepForm.query';
import { createConsultFirstStepData } from './CreateConsultFirstStepForm.data';
import { getCreateConsultFirstStepSchema } from './CreateConsultFirstStepForm.schema';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useActiveNafList } from '../../../../../../hooks/fetchers/useActiveNafList/useActiveNafList';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import { ConsultationType } from '../../../../../../types/models/consultations/consultation.types';
import { useAddressAutocomplete } from '../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete';
import { useCurrentEntity } from '../../../../../../hooks/fetchers/useCurrentEntity/useCurrentEntity';
import departements from '../../../../../../assets/json/departements-region.json';
import { AddressSuggestion } from '../../../../../../types/models/address/address.types';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import Input from '../../../../../commons/Input/Input';
import Select from '../../../../../commons/Select/Select';
import Button from '../../../../../designSystem/Button/Button';
import { ButtonSize } from '../../../../../designSystem/Button/Button.types';
import { StepWizardContext } from '../../../../../commons/StepWizard/StepWizard.context';
import { useConsultationSummary } from '../../../../../../hooks/fetchers/useConsultationSummary/useConsultationSummary';
import { AVAILABLE_COMPANIES_QUERY_KEY } from '../../../../../../hooks/fetchers/useAvailableCompanies/useAvailableCompanies';
import { queryAvailableCompanies } from '../../../../../../hooks/fetchers/useAvailableCompanies/useAvailableCompanies.query';
// import { getDomainIdFromConsultationType } from '../../../../../../utils/helpers/consultation/getDomainIdFromConsultationType';
import { UseAvailableCompaniesResponse } from '../../../../../../hooks/fetchers/useAvailableCompanies/useAvailableCompanies.types';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Loading from '../../../../../commons/Loading/Loading';
import { useDomainsList } from '../../../../../../hooks/fetchers/useDomainsList/useDomainsList';

export default () => {
  const { consultationId } = useParams();
  const [searchParams] = useSearchParams();

  const [duplicate] = useMemo(
    () => [searchParams.get('duplicate')],
    [searchParams]
  );

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { setCurrentStep } = useContext(StepWizardContext);

  const [selectedType, setSelectedType] = useState<ConsultationType>();
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const [defaultFieldOfActivity, setDefaultFieldOfActivity] = useState<string>();

  const [inputValue, setInputValue] = useState<string>();

  const [formInfos, setFormInfos] = useState<CreateConsultFirstStepFormSchema>();

  const { data: addressSuggestions } = useAddressAutocomplete(searchInputValue);

  const { data: NAFList, isLoading: isNAFLoading } = useActiveNafList();

  const { data: domainsList, isLoading: isDomainsLoading } = useDomainsList();

  const { data: currentEntity } = useCurrentEntity();

  const { data: consultationSummary, isLoading: isConsultationSummaryLoading } =
    useConsultationSummary(parseInt(consultationId as string), {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(consultationId),
      cacheTime: 0,
    });

  const {
    data: availableCompanies,
    refetch: refetchAvailableCompanies,
    isLoading: isAvailableCompaniesLoading,
    isSuccess: isAvailableCompaniesSuccess,
  } = useQuery<UseAvailableCompaniesResponse, Error>(
    [AVAILABLE_COMPANIES_QUERY_KEY, formInfos],
    () => queryAvailableCompanies(formInfos as CreateConsultFirstStepFormSchema),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(formInfos),
      cacheTime: 0,
    }
  );

  const formProps = useForm<CreateConsultFirstStepFormSchema>({
    resolver: yupResolver(getCreateConsultFirstStepSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
    getValues,
  } = formProps;

  const address = useWatch({
    control,
    name: 'address',
  });

  const fieldOfActivity = useWatch({
    control,
    name: 'fieldOfActivity',
  });

  useEffect(() => {
    if (consultationSummary && consultationId) {
      setValue('title', consultationSummary.infos.title, { shouldValidate: true });
      setValue('ref', consultationSummary.infos.ref || '', { shouldValidate: true });
      // setValue('type', consultationSummary.infos.type, { shouldValidate: true });
      setSelectedType(consultationSummary.infos.type);
      setValue('fieldOfActivity', consultationSummary.infos.NAF_code, { shouldValidate: true });
      setValue('useActualAddress', true, { shouldValidate: true });
      setValue('address', consultationSummary.infos.address as string, { shouldValidate: true });
      setValue('city', consultationSummary.infos.city as string, { shouldValidate: true });
      setValue('postalCode', consultationSummary.infos.postalCode as string, {
        shouldValidate: true,
      });
      setValue('department', consultationSummary.infos.department as string, {
        shouldValidate: true,
      });
      setValue('region', consultationSummary.infos.region as string, { shouldValidate: true });
      if (consultationSummary.infos.is_premium !== null) {
        setValue('is_premium', true);
      }
      // setSelectedType(consultationSummary.infos.type)
    }
  }, [consultationSummary, consultationId]);

  useEffect(() => {
    if (isValid) {
      const formValues = getValues();
      setFormInfos(formValues);
      refetchAvailableCompanies();
    }
  }, [isValid, address, fieldOfActivity]);

  const { mutate } = useMutation<
    Awaited<ReturnType<CreateConsultFirstStepMutation>>,
    Error,
    CreateConsultFirstStepFormSchema
  >(
    (data) =>
      consultationId
        ? queryUpdateConsultation(parseInt(consultationId as string), data)
        : queryInitConsultation(data),
    {
      mutationKey: INIT_CONSULTATION_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: ({ consultationId }) => {
        setCurrentStep(2);
        if (!duplicate) {
          navigate(`/dashboard/nouvelle_consultation/${consultationId}`);
        }
      },
      onError: (error) => {
        alert(error.message);
      },
    }
  );

  const onAddressChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const value = e.target.value;
    onChange(e);
    setSearchInputValue(value);
  };

  const handleSelectedSuggestion = (suggestion: AddressSuggestion) => {
    setSearchInputValue('');
    setValue('address', suggestion.address, { shouldValidate: true });
    setValue('city', suggestion.city, { shouldValidate: true });
    setValue('postalCode', suggestion.postalCode, { shouldValidate: true });
    setValue('department', suggestion.department, { shouldValidate: true });
    setValue(
      'region',
      departements.find((res) => res.num_dep.toString() == suggestion.department_code)
        ?.region_name || '',
      { shouldValidate: true }
    );
  };

  useEffect(() => {
    if (isAvailableCompaniesSuccess) {
      setValue('is_premium', undefined);
    }
  }, [isAvailableCompaniesSuccess]);

  const onSubmit = handleSubmit((data) => mutate(data));

  const currentFormData = createConsultFirstStepData.map((row) =>
    row.filter((item) => {
      if (getCreateConsultFirstStepSchema['_nodes'].includes(item.name)) {
        return item;
      }
    })
  );

  useEffect(() => {
    if (NAFList && consultationSummary) {
      console.log(
        'eyyy',
        NAFList?.find(
          (item) => item.NAF_code === (consultationSummary.infos.NAF_code as any as number)
        )?.description
      );
      setDefaultFieldOfActivity(
        NAFList.find(
          (item) => item.NAF_code === (consultationSummary.infos.NAF_code as any as number)
        )?.description
      );
    }
  }, [NAFList, consultationSummary]);

  const updatedFormData = useMemo(
    () =>
      currentFormData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'fieldOfActivity':
              return {
                ...item,
                options: NAFList
                  // ?.filter((item) => {
                  //   if (selectedType) {
                  //     return selectedType as any === item.id_domain;
                  //   }
                  // })
                  ?.filter((item) => item.description.toLowerCase().includes(inputValue?.toLowerCase() || 'Not in list'))
                  ?.map(({ NAF_code, description }) => ({
                    label: description,
                    value: NAF_code,
                  })),
                defaultValue: consultationSummary ? {
                  label: defaultFieldOfActivity || '',
                  value: consultationSummary?.infos.NAF_code,
                } : undefined,
              };
            // case 'type':
            //   return {
            //     ...item,
            //     options: domainsList?.map(({ id, name }) => ({
            //       label: name,
            //       value: id,
            //     })),
            //     // @ts-ignore
            //     onChangeValue: (value: string) => {
            //       setSelectedType(value as ConsultationType);
            //     },
            //     ...(consultationSummary && {
            //       defaultValue: {
            //         label: Object.values(ConsultationType).find(
            //           (item) => item === consultationSummary.infos.type
            //         ),
            //         value: Object.values(ConsultationType).find(
            //           (item) => item === consultationSummary.infos.type
            //         ),
            //       },
            //     }),
            //   };
            case 'useActualAddress':
              return {
                ...item,
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  const isChecked = Boolean(e.target.checked);
                  if (isChecked && currentEntity) {
                    setValue('address', currentEntity.address, { shouldValidate: true });
                    setValue('city', currentEntity.city, { shouldValidate: true });
                    setValue('postalCode', currentEntity.postalCode, { shouldValidate: true });
                    setValue('department', currentEntity.department, { shouldValidate: true });
                    setValue('region', currentEntity.region, { shouldValidate: true });
                  }
                },
              };
            case 'address':
              return {
                ...item,
                label:
                  selectedType === ConsultationType.Fournitures
                    ? 'Adresse de livraison'
                    : "Adresse du lieu d'execution",
              };
            default:
              return item;
          }
        })
      ),
    [currentFormData, NAFList, domainsList, inputValue, selectedType, consultationSummary, defaultFieldOfActivity]
  );

  const { isLoading } = useMergedStatesFromFetchers({
    loadings: [isNAFLoading, isDomainsLoading, isConsultationSummaryLoading],
  });

  if (isLoading) return <Loading />;

  return (
    <form className="form">
      {updatedFormData.map((row, rowIndex) => (
        <div key={rowIndex} className="form__row">
          {row.map(
            ({ name, placeholder, label, type, options, onChangeValue, ...props }, inputIndex) =>
              !(name === 'is_premium' && availableCompanies && availableCompanies?.count > 3) && (
                <Controller
                  key={inputIndex}
                  name={name}
                  render={({ field: { onChange, value, ...rest } }) =>
                    name === 'address' ? (
                      <SearchInput
                        suggestions={addressSuggestions}
                        placeholder={placeholder || ''}
                        type={type}
                        onChange={(e) => onAddressChange(e, onChange)}
                        error={errors[name]?.message as string}
                        // @ts-ignore
                        value={value}
                        {...{ name, label, handleSelectedSuggestion }}
                        {...props}
                      />
                    ) : type !== 'select' ? (
                      <Input
                        placeholder={placeholder || ''}
                        type={type}
                        disabled={Boolean(
                          ['department', 'region', 'city', 'postalCode'].includes(name)
                        )}
                        error={errors[name]?.message as string}
                        // @ts-ignore
                        value={value}
                        {...(name === 'is_premium' &&
                          value && {
                            checked: true,
                          })}
                        {...{ name, onChange, label }}
                        {...props}
                      />
                    ) : (
                      <Select
                        placeholder={placeholder || ''}
                        error={errors[name]?.message as string}
                        onInputChange={(value: string) => setInputValue(value)}
                        {...{ label, options, ...rest }}
                        {...props}
                        // @ts-ignore
                        onChange={({ value }: SelectOption) => {
                          onChange(value);
                          if (onChangeValue) {
                            onChangeValue(value);
                          }
                        }}
                        // @ts-ignore
                        noOptionsMessage={({ inputValue }) => !inputValue ? 'Chercher un domaine' : 'Aucun résultat trouvé'}
                      />
                    )
                  }
                  {...{ control }}
                />
              )
          )}
        </div>
      ))}
      {isValid && availableCompanies && availableCompanies.count > 3 && (
        <div className="form__row" style={{ marginBottom: 'var(--spacing-sToM)' }}>
          <span style={{ color: 'var(--gray-4)' }}>
            {availableCompanies.count} entreprises ciblées
          </span>
        </div>
      )}
      <Button
        style={{ alignSelf: 'unset' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        size={ButtonSize.Large}
        disabled={!isValid}
        isLoading={isAvailableCompaniesLoading}>
        Suivant
      </Button>
    </form>
  );
};
